import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { loadSession, clearSession, displayName, AuthSession, isPlatformAdmin } from "./lib/api.js";
import { useWorkspacePermissions } from "./lib/usePermissions.js";

import Landing from "./pages/Landing.js";
import Login from "./pages/Login.js";
import Signup from "./pages/Signup.js";
import Dashboard from "./pages/Dashboard.js";
import DhisInstances from "./pages/DhisInstances.js";
import AppClients from "./pages/AppClients.js";
import Workspaces from "./pages/Workspaces.js";
import Team from "./pages/Team.js";
import Subscriptions from "./pages/Subscriptions.js";
import Billing from "./pages/Billing.js";
import Datasets from "./pages/Datasets.js";
import DataPreview from "./pages/DataPreview.js";
import Portals from "./pages/Portals.js";
import Jobs from "./pages/Jobs.js";
import StreamConsole from "./pages/StreamConsole.js";
import LiveMonitor from "./pages/LiveMonitor.js";
import Security from "./pages/Security.js";
import SelectInstance from "./pages/SelectInstance.js";
import SettingsPage from "./pages/Settings.js";
import Audit from "./pages/Audit.js";
import Forwarding from "./pages/Forwarding.js";
import Support from "./pages/Support.js";

import AdminLayout from "./layouts/AdminLayout.js";
import WorkspaceAdministrationArea from "./layouts/WorkspaceAdministrationArea.js";
import PlatformAdministrationArea from "./layouts/PlatformAdministrationArea.js";
import ComingSoon from "./pages/ComingSoon.js";
import EnvironmentBadge from "./layouts/EnvironmentBadge.js";
import TransferLogs from "./pages/TransferLogs.js";
import TallyMetrics from "./pages/TallyMetrics.js";
import RollupsIndicators from "./pages/RollupsIndicators.js";
import MappingConsole from "./pages/MappingConsole.js";
import Observability from "./pages/Observability.js";
import MetricsExplorer from "./pages/MetricsExplorer.js";
import SmtpSettings from "./pages/SmtpSettings.js";
import Watermarks from "./pages/Watermarks.js";
import DeadLetterInspector from "./pages/DeadLetterInspector.js";
import SyncedSourceData from "./pages/SyncedSourceData.js";
import Reconciliation from "./pages/Reconciliation.js";
import HelpDocs from "./pages/HelpDocs.js";
import ReplayRepair from "./pages/ReplayRepair.js";
import StreamConsoleAdmin from "./pages/StreamConsoleAdmin.js";
import WarehouseExplorer from "./pages/WarehouseExplorer.js";
import LineageExplorer from "./pages/LineageExplorer.js";
import EffectivePermissions from "./pages/EffectivePermissions.js";
import AccessAudit from "./pages/AccessAudit.js";
import TenancyDetail from "./pages/TenancyDetail.js";
import MonetisationOverview from "./pages/MonetisationOverview.js";
import SettingsForm from "./pages/SettingsForm.js";
import CaptureModule from "./pages/CaptureModule.js";
import CaptureFormBuilder from "./pages/CaptureFormBuilder.js";
import OnboardingChecklist from "./pages/OnboardingChecklist.js";
import NavigationSearch from "./layouts/NavigationSearch.js";
import { useSidebarCollapsed } from "./layouts/useSidebarCollapsed.js";
import { workspaceNavigation } from "./navigation/workspaceNavigation.js";
import { filterNavTree } from "./navigation/permissions.js";
import { platformAdminNavigation } from "./navigation/platformAdminNavigation.js";
import AdminOverview from "./pages/admin/AdminOverview.js";
import AdminWorkspaces from "./pages/admin/AdminWorkspaces.js";
import AdminMembers from "./pages/admin/AdminMembers.js";
import AdminPlans from "./pages/admin/AdminPlans.js";
import AdminPaymentGateways from "./pages/admin/AdminPaymentGateways.js";
import AdminBilling from "./pages/admin/AdminBilling.js";
import AdminInstances from "./pages/admin/AdminInstances.js";
import AdminAppClients from "./pages/admin/AdminAppClients.js";
import AdminDatasets from "./pages/admin/AdminDatasets.js";
import AdminDhisMetadata from "./pages/admin/AdminDhisMetadata.js";
import AdminPortals from "./pages/admin/AdminPortals.js";
import AdminJobs from "./pages/admin/AdminJobs.js";
import AdminSecrets from "./pages/admin/AdminSecrets.js";
import AdminAudit from "./pages/admin/AdminAudit.js";
import AdminMailTemplates from "./pages/admin/AdminMailTemplates.js";
import AdminPipelineHealth from "./pages/admin/AdminPipelineHealth.js";
import { LabelsProvider } from "./lib/labels.js";
import AdminAggregateDomains from "./pages/admin/AdminAggregateDomains.js";
import AdminMetadataSnapshots from "./pages/admin/AdminMetadataSnapshots.js";
import AdminAppInstallations from "./pages/admin/AdminAppInstallations.js";
import AdminPortalDatasets from "./pages/admin/AdminPortalDatasets.js";
import AdminPublicationRuns from "./pages/admin/AdminPublicationRuns.js";
import AdminPublicationTargets from "./pages/admin/AdminPublicationTargets.js";
import AdminReconciliation from "./pages/admin/AdminReconciliation.js";
import AdminAnalytics from "./pages/admin/AdminAnalytics.js";
import AdminPublications from "./pages/admin/AdminPublications.js";
import AdminRoles from "./pages/admin/AdminRoles.js";
import AdminSettings from "./pages/admin/AdminSettings.js";
import AdminFeatureFlags from "./pages/admin/AdminFeatureFlags.js";
import AdminSecurityCenter from "./pages/admin/AdminSecurityCenter.js";
import AdminSupportConsole from "./pages/admin/AdminSupportConsole.js";
import AdminHealth from "./pages/admin/AdminHealth.js";
import AdminUserRoles from "./pages/admin/AdminUserRoles.js";
import AdminForwardingOversight from "./pages/admin/AdminForwardingOversight.js";

function Protected({ children, require }: { children: React.ReactNode; require?: "tenant" | "platform-admin" }) {
  const location = useLocation();
  const session = loadSession();
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  const admin = isPlatformAdmin(session);
  if (require === "platform-admin" && !admin) {
    return <Navigate to="/app" replace />;
  }
  if (require === "tenant" && admin) {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
}

function Avatar({ name }: { name: string }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return <span className="avatar" aria-hidden>{initial}</span>;
}

function Shell({ session, children }: { session: AuthSession; children: React.ReactNode }) {
  const navigate = useNavigate();
  const onSignOut = () => {
    clearSession();
    navigate("/login", { replace: true });
  };
  const who = displayName();
  const { has, hasAny, loading, isSuperAdmin } = useWorkspacePermissions(session.workspaceId);
  const { collapsed, toggle } = useSidebarCollapsed();
  const resolver = { isSuperAdmin, has, hasAny };

  // Sidebar is driven by workspaceNavigation (the IA single source of truth).
  // filterNavTree honours per-item / per-group permission gates; the
  // PLATFORM_SUPER_ADMIN and WORKSPACE_OWNER short-circuits in
  // useWorkspacePermissions / RBAC_ROLE_PERMISSIONS guarantee a workspace
  // admin sees every workspace page — narrow roles still only see what they
  // hold permissions for.
  const filteredNav = filterNavTree(workspaceNavigation, resolver);
  const sidebarGroups = filteredNav.groups;

  return (
    <div className="shell">
      <header className="shell-header">
        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={toggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >☰</button>
        <Link to="/app" className="brand">
          <img src="/brand-mark-light.svg" alt="" />
          <span>TallyStream</span>
        </Link>
        <span className="header-chip">
          {session.workspaceId ? `Workspace: ${session.workspaceId}` : "Default workspace"}
        </span>
        <span className="header-chip">
          Auth: {session.authType === "dhis2" ? "DHIS2" : "Local"}
        </span>
        <EnvironmentBadge />
        <div className="shell-header-spacer" />
        <button
          type="button"
          className="header-search-trigger"
          onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "/" }))}
          title="Search (press / )"
          aria-label="Search navigation"
        >
          <span aria-hidden>🔍</span>
          <kbd>/</kbd>
        </button>
        <span className="user-pill">
          <Avatar name={who} />
          <span>{who}</span>
          <span className="pill pill-info" style={{ textTransform: "uppercase" }}>{session.role}</span>
        </span>
        <button className="signout" type="button" onClick={onSignOut}>Sign out</button>
      </header>
      <NavigationSearch trees={[workspaceNavigation, platformAdminNavigation]} resolver={resolver} />

      {/*
        Workspace sidebar — IA principle:
          TOP = TallyStream's user-facing value (watch data flow in, browse
                what's been tallied, publish, audit what happened in your data).
          BOTTOM = an "Administration" group (collapsible <details>) holding
                every backend / configuration / billing concern. A workspace
                analyst rarely needs admin nav; a workspace owner expands it
                when they need to wire DHIS2 instances or invite teammates.
        Each nav item is gated on the workspace permission its page requires.
      */}
      <aside className="shell-sidebar" aria-label="Workspace navigation">
        {loading ? <div className="sidebar-section soft" style={{ padding: 12 }}>Loading…</div> : null}

        {sidebarGroups.map((group) => (
          <div key={group.id}>
            <div className="sidebar-section">
              <div className="sidebar-section-title">{group.label}</div>
            </div>
            <nav className="sidebar-nav">
              {group.items.map((item) => {
                // Dashboard is the workspace index route; everything else is
                // a regular sub-route. `end` only applies to the index match.
                const isIndex = item.to === "/app";
                return (
                  <NavLink key={item.to} to={item.to} end={isIndex}>
                    {item.label}
                    {item.status === "coming-soon" ? (
                      <span className="pill pill-info" style={{ marginLeft: 8, fontSize: 10 }}>soon</span>
                    ) : null}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        ))}

      </aside>

      <main className="shell-main">{children}</main>
    </div>
  );
}

function AppArea() {
  const [session, setSession] = useState<AuthSession | null>(loadSession());
  useEffect(() => {
    const onStorage = () => setSession(loadSession());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  if (!session) return <Navigate to="/login" replace />;
  return (
    <LabelsProvider>
    <Shell session={session}>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="select-instance" element={<SelectInstance />} />
        <Route path="workspaces" element={<Workspaces />} />
        <Route path="datasets" element={<Datasets />} />
        <Route path="data-preview" element={<DataPreview />} />
        <Route path="portals" element={<Portals />} />
        <Route path="stream-console" element={<StreamConsole />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="live" element={<LiveMonitor />} />
        <Route path="audit" element={<Audit />} />
        <Route path="support" element={<Support />} />
        <Route path="data-activity/transfer-logs" element={<TransferLogs scope="workspace" />} />
        <Route path="data-activity/synced-data" element={<SyncedSourceData />} />
        <Route path="data-activity/watermarks" element={<Watermarks />} />
        <Route path="data-activity/failures" element={<DeadLetterInspector />} />
        <Route path="tallying/tallymetrics" element={<TallyMetrics scope="workspace" view="values" />} />
        <Route path="metadata/tally-variables" element={<TallyMetrics scope="workspace" view="catalog" />} />
        <Route path="tallying/rollups" element={<RollupsIndicators scope="workspace" />} />
        <Route path="tallying/indicators" element={<RollupsIndicators scope="workspace" />} />
        <Route path="tallying/mappings" element={<MappingConsole scope="workspace" />} />
        <Route path="tallying/reconciliation" element={<Reconciliation scope="workspace" />} />
        <Route path="tallying/aggregate-domains" element={<AdminAggregateDomains />} />
        <Route path="insights/analytics" element={<AdminAnalytics />} />
        <Route path="insights/warehouse" element={<WarehouseExplorer />} />
        <Route path="insights/lineage" element={<LineageExplorer />} />
        <Route path="admin/dhis2/secrets" element={<AdminSecrets />} />
        <Route path="admin/dhis2/metadata-browser" element={<AdminDhisMetadata mode="workspace" />} />
        <Route path="admin/workspace/usage" element={<SettingsForm view="environment" />} />
        <Route path="admin/settings/pipeline" element={<SettingsForm view="pipeline" />} />
        <Route path="admin/settings/notifications" element={<SettingsForm view="notifications" />} />
        <Route path="capture" element={<CaptureModule view="dashboard" />} />
        <Route path="capture/forms" element={<CaptureModule view="forms" />} />
        <Route path="capture/forms/new" element={<CaptureFormBuilder />} />
        <Route path="capture/drafts" element={<CaptureModule view="drafts" />} />
        <Route path="capture/submissions" element={<CaptureModule view="submissions" />} />
        <Route path="capture/review" element={<CaptureModule view="review" />} />
        <Route path="capture/analytics" element={<CaptureModule view="analytics" />} />
        <Route path="capture/settings" element={<CaptureModule view="settings" />} />
        <Route path="help/onboarding" element={<OnboardingChecklist />} />
        <Route path="help/docs" element={<HelpDocs />} />
        {/* Pre-Administration-shell paths — preserved as redirects so old
            bookmarks/links land on the same page inside the new layout. */}
        <Route path="instances"     element={<Navigate to="/app/administration/connections/instances" replace />} />
        <Route path="app-clients"   element={<Navigate to="/app/administration/connections/app-clients" replace />} />
        <Route path="forwarding"    element={<Navigate to="/app/administration/connections/forwarding" replace />} />
        <Route path="team"          element={<Navigate to="/app/administration/workspace/team" replace />} />
        <Route path="subscriptions" element={<Navigate to="/app/administration/workspace/subscriptions" replace />} />
        <Route path="billing"       element={<Navigate to="/app/administration/workspace/billing" replace />} />
        <Route path="settings"      element={<Navigate to="/app/administration/settings/general" replace />} />
        <Route path="security"      element={<Navigate to="/app/administration/settings/security" replace />} />
        {/* Catch-all: render the structured stub for nav items whose
            page hasn't shipped yet, so the IA is fully reachable. */}
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    </Shell>
    </LabelsProvider>
  );
}

function AdminArea() {
  const [session, setSession] = useState<AuthSession | null>(loadSession());
  useEffect(() => {
    const onStorage = () => setSession(loadSession());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  if (!session) return <Navigate to="/login" replace />;
  return (
    <LabelsProvider>
    <AdminLayout session={session}>
      <Routes>
        <Route index element={<AdminOverview />} />
        <Route path="workspaces" element={<AdminWorkspaces />} />
        <Route path="workspaces/:workspaceId" element={<AdminWorkspaces />} />
        {/* Pages that LIVE in the new Administration shell — old paths
            redirect so existing bookmarks land at the same page inside
            the new layout. */}
        <Route path="members"          element={<Navigate to="/admin/administration/identity/members" replace />} />
        <Route path="roles"            element={<Navigate to="/admin/administration/identity/roles" replace />} />
        <Route path="user-roles"       element={<Navigate to="/admin/administration/identity/user-roles" replace />} />
        <Route path="plans"            element={<Navigate to="/admin/administration/monetisation/plans" replace />} />
        <Route path="packages"         element={<Navigate to="/admin/administration/monetisation/plans" replace />} />
        <Route path="payment-gateways" element={<Navigate to="/admin/administration/monetisation/payment-gateways" replace />} />
        <Route path="billing"          element={<Navigate to="/admin/administration/monetisation/billing" replace />} />
        <Route path="instances"        element={<Navigate to="/admin/administration/dhis2/instances" replace />} />
        <Route path="connections"      element={<Navigate to="/admin/administration/dhis2/instances" replace />} />
        <Route path="app-clients"      element={<Navigate to="/admin/administration/dhis2/app-clients" replace />} />
        <Route path="forwarding"       element={<Navigate to="/admin/administration/dhis2/forwarding" replace />} />
        <Route path="secrets"          element={<Navigate to="/admin/administration/operations/secrets" replace />} />
        <Route path="audit"            element={<Navigate to="/admin/administration/operations/audit" replace />} />
        <Route path="feature-flags"    element={<Navigate to="/admin/administration/operations/feature-flags" replace />} />
        <Route path="security-center"  element={<Navigate to="/admin/administration/operations/security-center" replace />} />
        <Route path="support"          element={<Navigate to="/admin/administration/operations/support" replace />} />
        <Route path="health"           element={<Navigate to="/admin/administration/operations/health" replace />} />
        <Route path="settings"         element={<Navigate to="/admin/administration/settings/general" replace />} />
        {/* Pages that REMAIN in the platform-overview surface (not admin shell). */}
        <Route path="datasets" element={<AdminDatasets />} />
        <Route path="dhis-metadata" element={<AdminDhisMetadata />} />
        <Route path="dhis2/secrets" element={<AdminSecrets />} />
        <Route path="dhis2/metadata-snapshots" element={<AdminMetadataSnapshots />} />
        <Route path="dhis2/app-installations" element={<AdminAppInstallations />} />
        <Route path="portals/datasets" element={<AdminPortalDatasets />} />
        <Route path="publications/runs" element={<AdminPublicationRuns />} />
        <Route path="publications/targets" element={<AdminPublicationTargets />} />
        <Route path="aggregate-domains" element={<AdminAggregateDomains />} />
        <Route path="reconciliation" element={<AdminReconciliation />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="publications" element={<AdminPublications />} />
        <Route path="portals" element={<AdminPortals />} />
        <Route path="jobs" element={<AdminJobs />} />
        <Route path="live" element={<LiveMonitor scope="platform" />} />
        <Route path="sync/transfer-logs" element={<TransferLogs scope="platform" />} />
        <Route path="data/tallymetrics" element={<TallyMetrics scope="platform" view="values" />} />
        <Route path="metadata/tally-variables" element={<TallyMetrics scope="platform" view="catalog" />} />
        <Route path="data/rollups-indicators" element={<RollupsIndicators scope="platform" />} />
        <Route path="data/mappings" element={<MappingConsole scope="platform" />} />
        <Route path="data/warehouse" element={<WarehouseExplorer />} />
        <Route path="data/lineage" element={<LineageExplorer />} />
        <Route path="access/effective-permissions" element={<EffectivePermissions />} />
        <Route path="access/audit" element={<AccessAudit />} />
        <Route path="workspaces/:workspaceId/activity" element={<TenancyDetail view="activity" />} />
        <Route path="workspaces/:workspaceId/usage" element={<TenancyDetail view="usage" />} />
        <Route path="workspaces/:workspaceId/support" element={<TenancyDetail view="support" />} />
        <Route path="workspaces/:workspaceId/billing" element={<TenancyDetail view="billing" />} />
        <Route path="monetisation/transactions" element={<MonetisationOverview view="transactions" />} />
        <Route path="monetisation/invoices" element={<MonetisationOverview view="invoices" />} />
        <Route path="monetisation/usage-quotas" element={<MonetisationOverview view="usage" />} />
        <Route path="settings/pipeline" element={<SettingsForm view="pipeline" />} />
        <Route path="settings/access-control" element={<SettingsForm view="access-control" />} />
        <Route path="settings/cors" element={<SettingsForm view="cors" />} />
        <Route path="settings/environment" element={<SettingsForm view="environment" />} />
        <Route path="settings/branding" element={<SettingsForm view="branding" />} />
        <Route path="operations/notifications" element={<SettingsForm view="notifications" />} />
        <Route path="operations/metrics" element={<MetricsExplorer />} />
        <Route path="operations/smtp" element={<SmtpSettings />} />
        <Route path="operations/mail-templates" element={<AdminMailTemplates />} />
        <Route path="operations/pipeline-health" element={<AdminPipelineHealth />} />
        <Route path="operations/observability" element={<Observability />} />
        <Route path="sync/watermarks" element={<Watermarks />} />
        <Route path="sync/dead-letter" element={<DeadLetterInspector />} />
        <Route path="sync/replay-repair" element={<ReplayRepair />} />
        <Route path="sync/stream-console" element={<StreamConsoleAdmin />} />
        {/* Catch-all: render the structured stub for nav items whose
            page hasn't shipped yet, so the IA is fully reachable. */}
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    </AdminLayout>
    </LabelsProvider>
  );
}

function WorkspaceAdministrationGate() {
  const [session, setSession] = useState<AuthSession | null>(loadSession());
  useEffect(() => {
    const onStorage = () => setSession(loadSession());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  if (!session) return <Navigate to="/login" replace />;
  return <WorkspaceAdministrationArea session={session} />;
}

function PlatformAdministrationGate() {
  const [session, setSession] = useState<AuthSession | null>(loadSession());
  useEffect(() => {
    const onStorage = () => setSession(loadSession());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  if (!session) return <Navigate to="/login" replace />;
  return <PlatformAdministrationArea session={session} />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      {/* Administration is its own full-page shell — must be matched
          BEFORE /app/* so the workspace shell doesn't capture it. */}
      <Route
        path="/app/administration/*"
        element={
          <Protected require="tenant">
            <WorkspaceAdministrationGate />
          </Protected>
        }
      />
      <Route
        path="/app/*"
        element={
          <Protected require="tenant">
            <AppArea />
          </Protected>
        }
      />
      {/* Platform Administration shell — must be matched BEFORE /admin/*. */}
      <Route
        path="/admin/administration/*"
        element={
          <Protected require="platform-admin">
            <PlatformAdministrationGate />
          </Protected>
        }
      />
      <Route
        path="/admin/*"
        element={
          <Protected require="platform-admin">
            <AdminArea />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
